<template>
    <app-loader v-if="loading"></app-loader>
    <application v-else/>
</template>

<script>
  import localforage from 'localforage';
  import moment from 'moment';
  import { gsap } from 'gsap';
  import AppLoader from "./components/app/AppLoader";
  import Application from "./views/Application";
  import { readLog, writeLog} from "./utils/logging";
  import { writeTrackLog } from "./utils/tracking";
  import {syncAndUpdate, prepareDevice, syncLocalWorkdaysToVue, syncLocalToRemote} from "./utils/sync";
  import {isOnline} from "./utils/online";

  export default {
    name: 'app',

    components: {Application, AppLoader},

    data() {
      return {
        loading: true,
        timer: null
      }
    },

    computed: {
      currentDevice() {
        return this.$store.getters.currentDevice
      },

      darkMode() {
        return this.$store.state.darkMode
      },

      deviceReady() {
        if (process.env.NODE_ENV === 'development') console.log('device is ready', this.$store.getters.currentDevice !== null && this.$store.getters.currentDevice !== undefined);
        return this.$store.getters.currentDevice !== null && this.$store.getters.currentDevice !== undefined
      },
    },

    watch: {
      currentDevice(val) {
        if (!val || !window.FirebasePlugin) return;

        if (!this.$store.state.fcmToken) {
          if (process.env.NODE_ENV === 'development') console.log('App:watch:currentDevice::getToken')
          window.FirebasePlugin.getToken(token => this.$store.dispatch('sendFCMTokenToBackend', token));
        }
      }
    },

    methods: {
      initialize() {
        const self = this;

        return localforage.ready()
            .then(() => {
              if (process.env.NODE_ENV === 'development') console.log('App:initialize::localforage ready')
              return prepareDevice(self)
            })
            .then(device => {
              if (process.env.NODE_ENV === 'development') console.log('App:initialize::Device prepared', device)
              if (!this.currentDevice && this.$route.name !== 'login') this.$router.push({name: 'login'});
              if (this.currentDevice && this.$route.name !== 'pin') this.$router.push({name: 'pin'});
              if (!this.currentDevice && this.$store.state.locations && this.$store.state.locations.length && this.$store.state.token) this.$router.push('choose-location');

              this.loading = false;

              return device;
            });
      },
      bindListeners() {
        window.events.$on('setFullscreen', val => this.$store.dispatch('setFullscreen', val));
        window.events.$on('error', errorMsg => this.$store.dispatch('setError', errorMsg));
        window.events.$on('success', successMsg => this.$store.dispatch('setSuccess', successMsg));

        window.events.$on('redirectToLogin', () => {
          this.$store.dispatch('clearCurrentEmployee');
          this.$router.push({name: 'login'});
        });

        window.events.$on('workdayUpdated', () => {
          const self = this;
          syncLocalWorkdaysToVue(self);
        });

        window.events.$on('syncLocalToRemote', () => {
          const self = this;
          syncLocalToRemote(self);
        })
      },
      async onDeviceReady() {
        // eslint-disable-next-line no-undef
        if (typeof cordova === 'undefined') return;

        if (process.env.NODE_ENV === 'development') console.log('App:onDeviceReady');
        writeTrackLog('APP STARTED');

        if (process.env.NODE_ENV === 'development') console.log('App:onDeviceReady::isOnline', this.$store.state.online, isOnline())
        if (isOnline()) this.onOnline();
        else this.$store.commit('setOffline');

        await this.initialize();

        // Application logging facilities
        window.logOb = null;
        window.trackingLogObj = null;

        // eslint-disable-next-line no-undef
        window.resolveLocalFileSystemURL(cordova.file.externalApplicationStorageDirectory, function (dir) {
          dir.getFile("log.txt", {create: true}, function (file) {
            window.logObj = file;
          });
          dir.getFile("tracking_log.txt", {create: true}, function (file) {
            window.trackingLogObj = file;
          });

          window.writeLog = writeLog;
          window.writeTrackLog = writeTrackLog;

          writeLog('START APP');
        });

        // eslint-disable-next-line no-undef
        cordova.plugins.backgroundMode.setDefaults({ silent: true })
        // eslint-disable-next-line no-undef
        cordova.plugins.backgroundMode.enable();
        // eslint-disable-next-line no-undef
        cordova.plugins.backgroundMode.on('enable', () => {
          writeTrackLog("BACKGROUND SERVICE ENABLED");
          console.log('App::BackgroundService enabled');
        });
        // eslint-disable-next-line no-undef
        cordova.plugins.backgroundMode.on('disable', () => {
          writeTrackLog("BACKGROUND SERVICE DISABLED");
          console.log('App::BackgroundService disabled');
        });
        // eslint-disable-next-line no-undef
        cordova.plugins.backgroundMode.on('activate', () => {
          console.log('App::toBackground');
          if (this.$store.state.enableTracking) writeTrackLog("APP TO BACKGROUND");
          this.onBackgroundActivated();
        });
        // eslint-disable-next-line no-undef
        cordova.plugins.backgroundMode.on('deactivate', () => {
          if (this.$store.state.enableTracking) writeTrackLog("APP TO FOREGROUND");
          console.log('App:toForeground');
          this.onBackgroundDeactivated();
        });
        // eslint-disable-next-line no-undef
        cordova.plugins.backgroundMode.on('failure', () => {
          if (this.$store.state.enableTracking) writeTrackLog("BACKGROUND SERVICE FAILURE");
          console.log('App::BackgroundService failed');
        });

        // Global cordova event listeners
        document.addEventListener('online', this.onOnline, false);
        document.addEventListener("offline", this.onOffline, false);
        document.addEventListener('resume', this.onResume, false);
        document.addEventListener('backbutton', this.onBackButtonPushed, false);

        this.checkBattery();


        window.FirebasePlugin.onTokenRefresh(token => {
          // console.log('App:onDeviceReady::FIREBASE::onTokenRefreshToken', token)
          if (this.$store.state.enableTracking) writeTrackLog("FIREBASE::onTokenRefresh: got new token: " + token);
          this.$store.dispatch('sendFCMTokenToBackend', token)
        }, error => {
          if (this.$store.state.enableTracking) writeTrackLog("FIREBASE::onTokenRefresh failed");
          // console.log("App:onDeviceReady::Failed to refresh token", error);
        });

        this.checkNotificationPermission(false);

        //Register handlers
        window.FirebasePlugin.onMessageReceived(message => {
          console.log('App:onDeviceReady::FIREBASE::Notification received', message)
          try {
            if (message.messageType === "notification") this.handleNotificationMessage(message);
            else this.handleDataMessage(message);
          } catch (e) {
            console.log("App:onDeviceReady::FIREBASE::Exception in onMessageReceived callback: " + e.message);
          }
        }, function (error) {
          console.log("App:onDeviceReady::FIREBASE::failed receiving FirebasePlugin message", error);
        });
      },

      onResume() {
        if (this.$store.state.enableTracking) writeTrackLog("RESUME APP");

        const self = this;

        if (isOnline()) syncAndUpdate(self);
      },

      onOnline() {
        this.$store.commit('setOnline')

        if (this.currentDevice) {
          if (this.$store.state.enableTracking) writeTrackLog("APP ONLINE");

          const self = this;

          this.$store.dispatch('syncResources', self)
              .then(res => {
            syncAndUpdate(self);
          });
        }
      },

      onOffline() {
        this.$store.commit('setOffline')
      },

      onBackButtonPushed(e) {
        e.preventDefault();

        if (this.$store.state.enableTracking) writeTrackLog("BACK from: ", this.$route.name);

        if (this.$route.name === 'pin' || this.$route.name === 'login') {
          if (this.$store.state.enableTracking) writeTrackLog("EXIT APP");
          navigator.app.exitApp();
        }
      },

      onBatteryStatus(battery) {
        console.log('App:onBatteryStatus', battery);
        console.log("Level: " + battery.level + " charging: " + battery.charging);

        this.$store.commit('setBatteryLevel', Math.round(battery.level*100));
        this.$store.commit('setBatteryCharging', battery.charging);
      },

      onBackgroundActivated() {
        console.log('App:onBackgroundActivated')
        // eslint-disable-next-line no-undef
        var plugin = cordova.plugins.backgroundMode;

        plugin.disableWebViewOptimizations();
      },

      onBackgroundDeactivated() {
        console.log('App:onBackgroundDeactivated')
      },

      getToken() {
        console.log('App::getToken()', this.$store.state.fcmToken)
        const self = this;

        // eslint-disable-next-line no-undef
        FirebasePlugin.getToken(function (token) {
          // console.log('Firebase::getToken', token)
          self.$store.dispatch('sendFCMTokenToBackend', token);
        }, function (error) {
          console.log("Failed to get FCM token", error);
        });
      },

      checkBattery() {
        var self = this;
        navigator.getBattery().then(function(battery) {
          self.onBatteryStatus(battery);
          battery.addEventListener('chargingchange', function() {
            self.onBatteryStatus(this);
          });
          battery.addEventListener('levelchange', function() {
            self.onBatteryStatus(this);
          });
        });
      },

      checkNotificationPermission(requested) {
        const self = this;

        // console.log('App:checkNotificationPermission', {requested, self})

        window.FirebasePlugin.hasPermission(function (hasPermission) {
          // console.log('App:checkNotificationPermission::hasPermission', {check: hasPermission, requested:requested})
          if (hasPermission) {
            // console.log('App:checkNotificationPermission::hasPermission: get token', self.getToken());

            if (self.$store.state.enableTracking) writeTrackLog("FIREBASE::hasPermission: get token");

            // eslint-disable-next-line no-undef
            window.FirebasePlugin.getToken(function (token) {
              // console.log('Firebase::getToken', token)
              self.$store.dispatch('sendFCMTokenToBackend', token);
            }, function (error) {
              console.log("Failed to get FCM token", error);
            });
          } else if (!requested) {
            if (self.$store.state.enableTracking) writeTrackLog("FIREBASE::hasPermission: No permission, now asking permission");

            window.FirebasePlugin.grantPermission(self.checkNotificationPermission.bind(this, true));
          }
        });
      },

      handleNotificationMessage(message) {
        if (this.$store.state.enableTracking) writeTrackLog("NOTIFICATION::handleNotificationMessage", message);

        var title;

        if (message.title) {
          title = message.title;
        } else if (message.notification && message.notification.title) {
          title = message.notification.title;
        } else if (message.aps && message.aps.alert && message.aps.alert.title) {
          title = message.aps.alert.title;
        }

        var body;

        if (message.body) {
          body = message.body;
        } else if (message.notification && message.notification.body) {
          body = message.notification.body;
        } else if (message.aps && message.aps.alert && message.aps.alert.body) {
          body = message.aps.alert.body;
        }

        var msg = "Notification message received";
        if (message.tap) {
          msg += " (tapped in " + message.tap + ")";
        }
        if (title) {
          msg += '; title=' + title;
        }
        if (body) {
          msg += '; body=' + body;
        }

        msg += ": " + JSON.stringify(message);

        if (this.$store.state.enableTracking) writeTrackLog("NOTIFICATION::received: " + msg);
        console.log('Notification received', msg);
      },

      handleDataMessage(message) {
        if (!message) return;
        if (message.forceSync) {
          if (this.$store.state.debug) writeLog(moment().toISOString() + "Syncing local data to remote API");
          if (this.$store.state.enableTracking) writeTrackLog("Force sync from backend");

          const self = this;
          // eslint-disable-next-line no-undef
          syncLocalToRemote(self, true, cordova.plugins.backgroundMode.isActive());
        }
        if (message.pushLogs) {
          readLog(this.$store);
        }
        if (message.syncResources) {
          if (this.$store.state.debug) writeLog(moment().toISOString() + "Syncing resources from remote API");
          if (this.$store.state.enableTracking) writeTrackLog("Force syncResources from backend");

          const self = this;
          this.$store.dispatch('syncResources', self)
              .then(res => {
                if (self.$route.name !== 'pin') self.$store.commit('setChecked', {name: 'pin'});
                else self.$store.commit('setChecked');

                return syncAndUpdate(self);
              })
        }
      }
    },

    mounted() {
      // console.log('Cordova undefined: ', typeof cordova === 'undefined');

      gsap.set('#app-loader', {height: '100%'});
      gsap.set('#app-loader .loader-logo', {height: '4rem', width: 'auto'});
      gsap.set('#app-loader .app-header-brand', {width: '100%'});
      gsap.set('.app-main', {width: '100%'});

      if (typeof cordova !== 'undefined') {
        document.addEventListener('deviceready', this.onDeviceReady, false);
      } else {
        // console.log('App:mounted::online', (typeof window.navigator.onLine === 'undefined' ? true : window.navigator.onLine, navigator.connection.type))
        if (typeof window.navigator.onLine === 'undefined') this.$store.commit('setOnline');
        else this.$store.commit(window.navigator.onLine ? 'setOnline' : 'setOffline');

        try {
          this.checkBattery();
        } catch (e) {
          console.log("App:mounted::checkBattery: " + e.message);
        }

        this.initialize();
      }

      this.bindListeners()
    }
  }
</script>
